import React, { useEffect, useState, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { requireLogin } from "../utils/authRedirect";
import { flyToCart } from "../utils/flyToCart";
import Header from "./Front/Header";
import Footer from "./Front/Footer";
import API_BASE_URL from "../Config";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";
import MobileBottomNav from "./MobileBottomNav";
import CityPicker from "./CityPicker";
import HomeLoader from "./HomeLoader";
import { isDoctorPublic } from "../utils/doctorVisibility";
import { getDoctorRatingSummary } from "../utils/doctorReviews";
import "./styles/home.css";
import "../styles/global.css";
// import "../styles/Doctor_DoctorBookings.css";


/* ═══════════════════════════════════════════════════════════════
   SKELETON COMPONENTS
═══════════════════════════════════════════════════════════════ */
const Skeleton = ({ width = "100%", height = 16, borderRadius = 8, style = {} }) => (
  <div className="hb-skeleton" style={{ width, height, borderRadius, ...style }} />
);
const ProductSkeleton = () => (
  <div className="product-card hb-skeleton-card">
    <div className="product-top" style={{ background: "#f5f5f5", height: 200, borderRadius: "12px 12px 0 0", overflow: "hidden" }}>
      <Skeleton width="100%" height={200} borderRadius={0} />
    </div>
    <div className="product-details w-100" style={{ padding: "12px" }}>
      <Skeleton width="60%" height={11} style={{ marginBottom: 8 }} />
      <Skeleton width="90%" height={14} style={{ marginBottom: 6 }} />
      <Skeleton width="75%" height={14} style={{ marginBottom: 12 }} />
      <Skeleton width="40%" height={18} style={{ marginBottom: 12 }} />
      <Skeleton width="100%" height={38} borderRadius={50} />
    </div>
  </div>
);
const CategorySkeleton = () => (
  <div className="hb-skeleton-card common-bg" style={{ borderRadius: 12, overflow: "hidden", height: 130 }}>
    <Skeleton width="100%" height={130} borderRadius={12} />
  </div>
);

/* ═══════════════════════════════════════════════════════════════
   SHARED AUTOCOMPLETE DROPDOWN  (portal — position:fixed)
═══════════════════════════════════════════════════════════════ */
const AutoDropdown = ({ show, results, position, onItemClick }) => {
  if (!show) return null;
  if (!results.products.length && !results.doctors.length && !results.treatments?.length && !results.clinics?.length) return null;

  return ReactDOM.createPortal(
    <div
      className="hb-drop"
      style={{ position: "fixed", top: position.bottom + 4, left: position.left, width: position.width, zIndex: 100000 }}
    >
      {/* ── Products ── */}
      {results.products.length > 0 && (
        <>
          <div className="hb-drop-label">Products</div>
          {results.products.slice(0, 6).map((p) => (
            <Link
              key={p.id}
              to={`/product/${p.slug}`}
              className="hb-drop-item"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onItemClick(p.name)}
            >
              <img className="hb-drop-img"
                src={p.images ? `${API_BASE_URL}public/productimages/${p.images.split(",")[0]}` : `${API_BASE_URL}public/noimage.jpg`}
                alt={p.name}
              />
              <div className="hb-drop-info">
                <span className="hb-drop-name">{p.name}</span>
                <span className="hb-drop-badge hb-badge-product">Product</span>
              </div>
            </Link>
          ))}
        </>
      )}

      {/* ── Doctors ── */}
      {results.doctors.length > 0 && (
        <>
          <div className="hb-drop-label">Doctors</div>
          {results.doctors.slice(0, 6).map((d) => (
            <Link
              key={d.id}
              to={`/doctordetails/${d.slug}`}
              className="hb-drop-item"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onItemClick(d.name)}
            >
              <img className="hb-drop-img"
                src={d.images ? `${API_BASE_URL}public/doctorimages/${d.images.split(",")[0]}` : `${API_BASE_URL}public/doctorimages/doctor-placeholder.jpg`}
                alt={d.name}
              />
              <div className="hb-drop-info">
                <span className="hb-drop-name">{d.name}</span>
                <span className="hb-drop-badge hb-badge-doctor">Doctor</span>
              </div>
            </Link>
          ))}
        </>
      )}

      {/* ── Treatments ── */}
      {results.treatments?.length > 0 && (
        <>
          <div className="hb-drop-label">Treatments</div>
          {results.treatments.slice(0, 6).map((t) => (
            <Link
              key={t.id}
              to={`/treatment/${t.slug}`}
              className="hb-drop-item"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onItemClick(t.name)}
            >
              <img className="hb-drop-img"
                src={t.image ? `${API_BASE_URL}public/treatment/${t.image.split(",")[0]}` : (t.icon ? `${API_BASE_URL}public/treatment_icon/${t.icon.split(",")[0]}` : `${API_BASE_URL}public/noimage.jpg`)}
                alt={t.name}
              />
              <div className="hb-drop-info">
                <span className="hb-drop-name">{t.name}</span>
                <span className="hb-drop-badge hb-badge-treatment">Treatment</span>
              </div>
            </Link>
          ))}
        </>
      )}

      {/* ── Clinics ── */}
      {results.clinics?.length > 0 && (
        <>
          <div className="hb-drop-label">Clinics</div>
          {results.clinics.slice(0, 6).map((c) => (
            <Link
              key={c.id}
              to={`/clinicdetails/${c.slug}`}
              className="hb-drop-item"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onItemClick(c.name)}
            >
              <img className="hb-drop-img"
                src={c.images ? `${API_BASE_URL}public/clinicimages/${c.images.split(",")[0]}` : `${API_BASE_URL}public/noimage.jpg`}
                alt={c.name}
              />
              <div className="hb-drop-info">
                <span className="hb-drop-name">{c.name}</span>
                <span className="hb-drop-badge hb-badge-clinic">Clinic</span>
              </div>
            </Link>
          ))}
        </>
      )}
    </div>,
    document.body
  );
};

/* ═══════════════════════════════════════════════════════════════
   HEADER SEARCH OVERLAY  (React-controlled input over original)
═══════════════════════════════════════════════════════════════ */
const HeaderSearchOverlay = ({ visible, anchorRect, allProductsRef, allDoctorsRef, allTreatmentsRef, allClinicsRef }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ products: [], doctors: [], treatments: [], clinics: [] });
  const [showDrop, setShowDrop] = useState(false);
  const [dropPos, setDropPos] = useState({ bottom: 0, left: 0, width: 0 });
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);
  const mouseInDrop = useRef(false);

  const updateDropPos = useCallback(() => {
    if (!wrapRef.current) return;
    const r = wrapRef.current.getBoundingClientRect();
    setDropPos({ bottom: r.bottom, left: r.left, width: r.width });
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!q.trim()) { setResults({ products: [], doctors: [], treatments: [], clinics: [] }); setShowDrop(false); return; }
    debounceRef.current = setTimeout(() => {
      const lq = q.toLowerCase();
      const res = {
        products: allProductsRef.current.filter((p) => p?.name?.toLowerCase().includes(lq)),
        doctors: allDoctorsRef.current.filter((d) => d?.name?.toLowerCase().includes(lq)),
        treatments: (allTreatmentsRef?.current || []).filter((t) => t?.name?.toLowerCase().includes(lq)),
        clinics: (allClinicsRef?.current || []).filter((c) => c?.name?.toLowerCase().includes(lq)),
      };
      setResults(res);
      setShowDrop(res.products.length > 0 || res.doctors.length > 0 || res.treatments.length > 0 || res.clinics.length > 0);
      updateDropPos();
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [q, updateDropPos]);

  useEffect(() => { if (!visible) { setQ(""); setShowDrop(false); } }, [visible]);

  // Keep the portal dropdown anchored to the input on scroll/resize, and
  // continuously while it is open, so it never drifts away from the box.
  useEffect(() => {
    window.addEventListener("scroll", updateDropPos, { passive: true, capture: true });
    window.addEventListener("resize", updateDropPos);
    return () => {
      window.removeEventListener("scroll", updateDropPos, { capture: true });
      window.removeEventListener("resize", updateDropPos);
    };
  }, [updateDropPos]);

  useEffect(() => {
    if (!showDrop) return;
    let frameId;
    const loop = () => {
      updateDropPos();
      frameId = requestAnimationFrame(loop);
    };
    frameId = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(frameId);
  }, [showDrop, updateDropPos]);

  if (!anchorRect) return null;
  return (
    <div
      ref={wrapRef}
      className="header-search-overlay"
      style={{
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? "auto" : "none",
      }}
    >
      <i className="fa-solid fa-search" style={{ position: "absolute", left: "14px", color: "rgb(157 132 68)", zIndex: 1, fontSize: "15px" }} />
      <input className="rounded-pill"
        type="text" value={q} placeholder="Search medicines, doctors, symptoms, treatments…" autoComplete="off"
        style={{ width: "100%", height: "100%", outline: "none", background: "white", fontSize: "inherit",   padding: "0 12px 0 44px", boxSizing: "border-box", border: "solid 1px rgb(221 221 221)",}}
        onChange={(e) => { setQ(e.target.value); updateDropPos(); }}
        onFocus={() => { if (q.trim()) { setShowDrop(true); updateDropPos(); } }}
        onBlur={() => { setTimeout(() => { if (!mouseInDrop.current) setShowDrop(false); }, 200); }}
        onKeyDown={(e) => e.key === "Escape" && setShowDrop(false)}
      />

      <div onMouseEnter={() => { mouseInDrop.current = true; }} onMouseLeave={() => { mouseInDrop.current = false; }}>
        <AutoDropdown show={showDrop} results={results} position={dropPos} onItemClick={(name) => { setQ(name); setShowDrop(false); }} />
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   HERO SEARCH
═══════════════════════════════════════════════════════════════ */
const HeroSearch = ({ allProductsRef, allDoctorsRef, allTreatmentsRef, allClinicsRef, navigate, categories, selected }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ products: [], doctors: [], treatments: [], clinics: [] });
  const [showDrop, setShowDrop] = useState(false);
  const [pos, setPos] = useState({ bottom: 0, left: 0, width: 0 });
  const wrapRef = useRef(null);
  const shellRef = useRef(null);
  const debounceRef = useRef(null);
  const mouseInDrop = useRef(false);


  const updatePos = useCallback(() => {
    const el = shellRef.current || wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // If the search box has scrolled out of the viewport (fully above,
    // fully below, or tucked behind the sticky header), close the dropdown
    // instead of leaving it floating with no visible anchor.
    const headerGuard = 160; // approx sticky header height (nav + location row)
    if (r.bottom <= headerGuard || r.top >= window.innerHeight) {
      setShowDrop(false);
      return;
    }
    setPos({ bottom: r.bottom, left: r.left, width: r.width });
  }, []);

  useEffect(() => {
    // capture:true so scroll events from any nested scrollable container
    // (not just the window) also reposition the dropdown.
    window.addEventListener("scroll", updatePos, { passive: true, capture: true });
    window.addEventListener("resize", updatePos);
    return () => {
      window.removeEventListener("scroll", updatePos, { capture: true });
      window.removeEventListener("resize", updatePos);
    };
  }, [updatePos]);

  // While the dropdown is open, keep it glued to the input on every frame.
  // This guarantees the (portal, position:fixed) dropdown never drifts away
  // from the search box while the page is being scrolled.
  useEffect(() => {
    if (!showDrop) return;
    let frameId;
    const loop = () => {
      updatePos();
      frameId = requestAnimationFrame(loop);
    };
    frameId = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(frameId);
  }, [showDrop, updatePos]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!q.trim()) { setResults({ products: [], doctors: [], treatments: [], clinics: [] }); setShowDrop(false); return; }
    debounceRef.current = setTimeout(() => {
      const lq = q.toLowerCase();
      const res = {
        products: allProductsRef.current.filter((p) => p?.name?.toLowerCase().includes(lq)),
        doctors: allDoctorsRef.current.filter((d) => d?.name?.toLowerCase().includes(lq)),
        treatments: (allTreatmentsRef?.current || []).filter((t) => t?.name?.toLowerCase().includes(lq)),
        clinics: (allClinicsRef?.current || []).filter((c) => c?.name?.toLowerCase().includes(lq)),
      };
      setResults(res);
      setShowDrop(res.products.length > 0 || res.doctors.length > 0 || res.treatments.length > 0 || res.clinics.length > 0);
      updatePos();
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [q, updatePos]);

  useEffect(() => {
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        const drop = document.querySelector(".hb-drop");
        if (!drop || !drop.contains(e.target)) setShowDrop(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const handleSearch = () => {
    let slug = "";
    const cat = categories.find((c) => c.id == selected);
    if (cat) slug = cat.slug;
    navigate(`/category/${slug}?search=${q}`);
    setShowDrop(false);
  };

  return (
    <div ref={wrapRef} style={{ position: "relative", width: "100%" }}>
      <div className="hc-hero-search-shell" ref={shellRef}>
        <i className="fa-solid fa-search hc-hero-search-icon" />
        <input
          type="text"
          className="form-control search-field rounded-pill hc-hero-search-input"
          placeholder="Search medicines, doctors, symptoms, treatments…"
          value={q} autoComplete="off"
          onChange={(e) => { setQ(e.target.value); updatePos(); }}
          onFocus={() => { if (q.trim()) { setShowDrop(true); updatePos(); } }}
          onBlur={() => { setTimeout(() => { if (!mouseInDrop.current) setShowDrop(false); }, 200); }}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
        />
        <button type="button" className="hc-hero-search-btn d-none" onClick={handleSearch} aria-label="Search">
          <i className="fa-solid fa-arrow-right" />
        </button>
      </div>

      <div onMouseEnter={() => { mouseInDrop.current = true; }} onMouseLeave={() => { mouseInDrop.current = false; }}>
        <AutoDropdown show={showDrop} results={results} position={pos} onItemClick={(name) => { setQ(name); setShowDrop(false); }} />
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════ */
const Home = () => {
  const navigate = useNavigate();
  const location = useLocation();

  /* ── Original state ── */
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [banners, setBanners] = useState([]);
  const [selected, setSelected] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [products, setProducts] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef(null);
  const [categoriesData, setCategoriesData] = useState([]);
  const [cartCount, setCartCount] = useState(0);
  const [doctors, setDoctors] = useState([]);
  const [wishlisted, setWishlisted] = useState(false);
  const [wishlist, setWishlist] = useState([]);
  const [wishlistCount, setWishlistCount] = useState(0);
  // const [userId, setUserId] = useState(localStorage.getItem("user_id"));

  const [userId, setUserId] = useState(localStorage.getItem("user_id"));


  /* ── Section loading flags ── */
  const [loadingTabProducts, setLoadingTabProducts] = useState(true);
  const [loadingDoctors, setLoadingDoctors] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingBrands, setLoadingBrands] = useState(true);
  const [loadingBanners, setLoadingBanners] = useState(true);

  const [homeTreatments, setHomeTreatments] = useState([]);
  const [loadingTreatments, setLoadingTreatments] = useState(true);

  /* ── Full-page premium loader ──
     Hides once the above-the-fold data (doctors, treatments, categories,
     banners) is ready. A minimum visible time keeps it from flashing on
     fast connections, and a safety timeout guarantees it always clears
     even if one API call hangs or fails. */
  const [showPageLoader, setShowPageLoader] = useState(true);
  const loaderMinTimeRef = useRef(Date.now());
  useEffect(() => {
    const dataReady = !loadingDoctors && !loadingTreatments && !loadingCategories && !loadingBanners;
    if (!dataReady) return undefined;
    const elapsed = Date.now() - loaderMinTimeRef.current;
    const remaining = Math.max(0, 900 - elapsed);
    const t = setTimeout(() => setShowPageLoader(false), remaining);
    return () => clearTimeout(t);
  }, [loadingDoctors, loadingTreatments, loadingCategories, loadingBanners]);
  useEffect(() => {
    const safety = setTimeout(() => setShowPageLoader(false), 6000);
    return () => clearTimeout(safety);
  }, []);
  useEffect(() => {
    document.body.style.overflow = showPageLoader ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [showPageLoader]);

  useEffect(() => {
    setLoadingTreatments(true);
    fetch(`${API_BASE_URL}api/treatments/home`)
      .then((res) => res.json())
      .then((data) => {
        setHomeTreatments(Array.isArray(data.data) ? data.data : []);
      })
      .catch((err) => console.log("Home treatments fetch error:", err))
      .finally(() => setLoadingTreatments(false));
  }, []);

  /* ═══════════════════════════════════════════════════════
     GLOBAL SEARCH REFS
     allProductsRef    ← ALL products from getproductsAll
     allDoctorsRef     ← ALL doctors fetched across every page
     allTreatmentsRef  ← ALL treatments (from settingsFront)
     allClinicsRef     ← ALL clinics (from settingsFront)
     These refs are passed to both HeroSearch + HeaderSearchOverlay
     so the autocomplete always searches the complete dataset.
  ═══════════════════════════════════════════════════════ */
  const allProductsRef = useRef([]);  // every product in the system
  const allDoctorsRef = useRef([]);  // every doctor in the system
  const allTreatmentsRef = useRef([]); // every treatment in the system
  const allClinicsRef = useRef([]);    // every clinic in the system

  /* Fetch ALL products for search (dedicated endpoint) */
  useEffect(() => {
    fetch(`${API_BASE_URL}api/getproductsAll`)
      .then((res) => res.json())
      .then((json) => {
        if (json?.status && Array.isArray(json.data)) {
          allProductsRef.current = json.data;
          setProducts(json.data); // keep original state in sync
        }
      })
      .catch((err) => console.log("Search products fetch error:", err));
  }, []);

  /* Fetch ALL doctors across all pages for search */
  useEffect(() => {
    const fetchAllDoctors = async () => {
      const allDocs = [];
      let page = 1;
      let lastPage = 1;


      
      try {

        const res = await fetch(`${API_BASE_URL}api/settingsFront/1`);
              const data = await res.json();
              if (data.status) {
                // `api/settingsFront/1` returns every doctor row as-is, including
                // ones an admin has switched to Inactive or left unapproved on
                // the Doctors admin page. Unlike api/doctorFrontList (used by
                // the doctor listing page), this endpoint does no visibility
                // filtering, so we filter it here — otherwise disabled/
                // unapproved doctors keep showing up on the homepage and in
                // the header/hero search suggestions even after being turned
                // off in the admin panel.
                const docList = (data.data.doctor || []).filter(isDoctorPublic);
                setDoctors(docList);
                allDoctorsRef.current = docList; // enable doctor autocomplete search

                // enable treatment + clinic autocomplete search (same payload,
                // no extra network round-trip)
                allTreatmentsRef.current = data.data.treatment || [];
                allClinicsRef.current = data.data.clinic || [];
              }

        // do {
        //   const res = await fetch(`${API_BASE_URL}api/doctorFrontList?page=${page}&minPrice=0&maxPrice=10000000`);
        //   const json = await res.json();

        //   if (!json.status) break;

        //   const data = json.data;
        //   allDocs.push(...(data.data || []));

        //   lastPage = data.last_page || 1;
        //   page++;
        // } while (page <= 1);

        // allDoctorsRef.current = allDocs;

        // // also seed the visible home-page doctors (first 6) from the full list
        // setDoctors(allDocs);
      } catch (err) {
        console.log("All-doctors fetch error:", err);
      } finally {
        setLoadingDoctors(false);
      }
    };

    fetchAllDoctors();
  }, []);

  /* ── Header overlay state ── */
  const [scrolled, setScrolled] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);
  const originalInputRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 500);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

   const [menuData, setMenuData] = useState(null);

  useEffect(() => {
      (async () => {
        try {
          const res = await fetch(`${API_BASE_URL}api/settingsFront/1`);
          const data = await res.json();
          if (data.status) setMenuData(data.data);
        } catch (err) { console.error(err); }
      })();
    }, []);

  useEffect(() => {
    const setup = () => {
      const input =
        document.querySelector('input[placeholder="Search Here"]') ||
        document.querySelector(".header-search input") ||
        document.querySelector("header input[type='text']") ||
        document.querySelector(".search-box input");
      if (!input) return false;
      originalInputRef.current = input;
      input.style.opacity = "0";
      input.style.pointerEvents = "none";
      input.style.userSelect = "none";
      const r = input.getBoundingClientRect();
      setAnchorRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      const onResize = () => {
        const r2 = input.getBoundingClientRect();
        setAnchorRect({ top: r2.top, left: r2.left, width: r2.width, height: r2.height });
      };
      window.addEventListener("resize", onResize);
      return () => window.removeEventListener("resize", onResize);
    };
    let cleanup = null;
    const run = () => { cleanup = setup() || null; };
    run();
    if (!originalInputRef.current) {
      const t1 = setTimeout(() => { run(); if (!originalInputRef.current) { const t2 = setTimeout(run, 800); return () => clearTimeout(t2); } }, 400);
      return () => clearTimeout(t1);
    }
    return () => { cleanup && cleanup(); };
  }, []);

  /* ════════════════════════════════════════════════════════
     ORIGINAL LOGIC — UNCHANGED
  ════════════════════════════════════════════════════════ */
  const handleWishlist = async (e, id) => {
    e.preventDefault();
    if (!requireLogin(navigate, location, { message: "Please login to use your wishlist" })) return;
    const userid = localStorage.getItem("user_id");
    try {
      const response = await fetch(`${API_BASE_URL}api/wishlist/add`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ product_id: id, userid }),
      });
      if (response.status === 201) toast.success(" Added to wishlist");
      else if (response.status === 409) toast.success(" Wishlist Deleted");
      getWishlist();
    } catch (error) { console.error("Wishlist error:", error); }
  };

  const getWishlist = () => {
    const userid = localStorage.getItem("user_id");
    fetch(`${API_BASE_URL}api/myWishlistData?userid=${userid}`)
      .then((res) => res.json())
      .then((json) => { if (json?.data) { setWishlist(Object.values(json.data)); setWishlistCount(json.data.length); } })
      .catch((err) => console.error(err));
  };

  useEffect(() => {
    if (!query) { setSuggestions([]); return; }
    setSuggestions(products.filter((p) => p?.name?.toLowerCase().includes(query.toLowerCase())));
  }, [query, products]);

  const handleSelect = (product) => { setQuery(product.name); setIsOpen(false); };

  /* Tab products (homepage grid only) */
  useEffect(() => {
    setLoadingTabProducts(true);
    fetch(`${API_BASE_URL}api/getproducts`)
      .then((res) => res.json())
      .then((json) => { if (json.status && Array.isArray(json.data)) setCategoriesData(json.data); })
      .catch((err) => console.log(err))
      .finally(() => setLoadingTabProducts(false));
  }, []);

  const filteredProducts =
    activeTab === "all"
      ? categoriesData.flatMap((c) => c.products)
      : categoriesData.find((c) => c.category.id.toString() === activeTab)?.products || [];

  /* Categories */
  useEffect(() => {
    setLoadingCategories(true);
    fetch(`${API_BASE_URL}api/categories_front`)
      .then((res) => res.json())
      .then((data) => { setCategories(Array.isArray(data) ? data : (data.data || [])); })
      .catch((err) => console.log(err))
      .finally(() => setLoadingCategories(false));
  }, []);

  /* Brands */
  useEffect(() => {
    setLoadingBrands(true);
    fetch(`${API_BASE_URL}api/brandData`)
      .then((res) => res.json())
      .then((data) => { setBrands(Array.isArray(data) ? data : (data.data || [])); })
      .catch((err) => console.log(err))
      .finally(() => setLoadingBrands(false));
  }, []);

  /* Banners */
  useEffect(() => {
    setLoadingBanners(true);
    fetch(`${API_BASE_URL}api/bannerData`)
      .then((res) => res.json())
      .then((data) => { setBanners(Array.isArray(data) ? data : (data.data || [])); })
      .catch((err) => console.log(err))
      .finally(() => setLoadingBanners(false));
  }, []);

  useEffect(() => { getWishlist(); }, []);

  let deviceId = localStorage.getItem("device_id");
  if (!deviceId) { deviceId = uuidv4(); localStorage.setItem("device_id", deviceId); }

  const addToCart = (id, qty) => {
    fetch(`${API_BASE_URL}api/addToCart`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ device_id: deviceId, id, qty }),
    })
      .then((res) => res.json())
      .then((json) => {
        const count = Object.keys(json.cart).length;
        setCartCount(count);
        const el = document.querySelector(".cartcount");
        if (el) el.innerText = count;
        window.dispatchEvent(new CustomEvent("cart:updated", { detail: { count } }));
      })
      .catch((err) => console.error("Add to cart error:", err));
  };

  /* ── Quick services (cosmetic — routes reuse existing pages) ── */
  const quickServices = [
    { icon: "fa-user-doctor", title: "Book Consultation", to: "/doctor-list-page" },
    { icon: "fa-pills", title: "Buy Medicines", to: "/ayush-product-list" },
    { icon: "fa-flask", title: "Health Products", to: "/category/vitamins" },
    { icon: "fa-spa", title: "Skin & Hair Care", to: "/category/mens-care" },
    { icon: "fa-venus", title: "Women's Health", to: "/category/women-care" },
    { icon: "fa-brain", title: "Mental Wellness", to: "/treatments" },
    { icon: "fa-weight-scale", title: "Weight Loss", to: "/treatments" },
    { icon: "fa-droplet", title: "Diabetes Care", to: "/treatments" },
    { icon: "fa-bone", title: "Joint Care", to: "/treatments" },
    { icon: "fa-shield-heart", title: "Immunity", to: "/treatments" },
  ];

  const stats = [
    { icon: "fa-users", value: "6L+", label: "Happy Patients" },
    { icon: "fa-user-doctor", value: "500+", label: "Expert Doctors" },
    { icon: "fa-award", value: "98%", label: "Satisfaction Rate" },
    { icon: "fa-headset", value: "24/7", label: "Customer Support" },
  ];

  const steps = [
    { no: "01", icon: "fa-magnifying-glass", title: "Search & Explore", desc: "Find symptoms, treatments and browse authentic Ayurvedic care." },
    { no: "02", icon: "fa-user-doctor", title: "Consult Experts", desc: "Connect with verified Ayurvedic doctors online or in-clinic." },
    { no: "03", icon: "fa-truck-medical", title: "Get Treatment", desc: "Receive personalised remedies delivered to your doorstep." },
  ];

  /* ── Static content for mockup sections ── */
  const symptomChips = ["Chennai", "Bangalore", "Hyderabad", "Pune", "Coimbatore", "Mysore", "Delhi", "Trichy", "Madurai", "Theni"];

  /* ── Real blog data (api/blogs_front — same endpoint as BlogList.js) ── */
  const [blogPosts, setBlogPosts] = useState([]);
  const [loadingBlog, setLoadingBlog] = useState(true);
  useEffect(() => {
    setLoadingBlog(true);
    const token = localStorage.getItem("authToken");
    fetch(`${API_BASE_URL}api/blogs_front`, { headers: { Accept: "application/json", Authorization: `Bearer ${token}` } })
      .then((res) => res.json())
      .then((data) => {
        if (data.status && Array.isArray(data.data)) {
          setBlogPosts(
            data.data.slice(0, 4).map((item) => ({
              title: item.name,
              desc: item.short_desc,
              slug: item.slug,
              img: item.blogimg ? `${API_BASE_URL}public/blog/${item.blogimg}` : `${API_BASE_URL}public/noimage.jpg`,
            }))
          );
        }
      })
      .catch((err) => console.log("Home blog fetch error:", err))
      .finally(() => setLoadingBlog(false));
  }, []);

  /* ── Testimonials (auto-rotating, self-contained) ── */
  const testimonials = [
    { name: "Priya Sharma", city: "Bangalore", stars: 5, text: "The doctors are very knowledgeable and the treatment is truly effective. My PCOS symptoms have improved a lot." },
    { name: "Arjun Menon", city: "Chennai", stars: 5, text: "Authentic Ayurvedic medicines delivered on time. The consultation was detailed and genuinely helpful." },
    { name: "Kavya Reddy", city: "Hyderabad", stars: 4, text: "Great experience overall. The health camp was well organised and the doctors were patient and caring." },
  ];
  const [testiIdx, setTestiIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTestiIdx((i) => (i + 1) % testimonials.length), 4500);
    return () => clearInterval(t);
  }, [testimonials.length]);

  /* ── Patient review video popup ── */
  const [showVideoModal, setShowVideoModal] = useState(false);
  const patientReviewVideoId = "wmwVs19lRpk";
  useEffect(() => {
    if (!showVideoModal) return;
    const onEsc = (e) => { if (e.key === "Escape") setShowVideoModal(false); };
    document.addEventListener("keydown", onEsc);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onEsc); document.body.style.overflow = ""; };
  }, [showVideoModal]);

  /* ── Health Programs (existing routes) ── */
  const healthPrograms = [
    { icon: "fa-house-medical", title: "Medical Camp", to: "/calendar" },
    { icon: "fa-calendar-day", title: "Health Event Days", to: "/event-day" },
    { icon: "fa-calendar-check", title: "Health Awareness Calendar", to: "/health-awareness-calender" },
  ];

  /* ── Location for the hero search pill (separate instance from Header's) ──
     Popular quick-pick chips shown before the user types anything in the
     hero CityPicker. Cosmetic only now — search itself covers any
     city/town/village via live Nominatim lookup, not just this list. */
  const tamilNaduCities = [
    "Chennai", "Coimbatore", "Madurai", "Trichy", "Salem", "Tirunelveli", "Erode",
    "Vellore", "Thoothukudi", "Dindigul", "Thanjavur", "Ranipet", "Sivakasi", "Karur",
    "Udhagamandalam", "Hosur", "Nagercoil", "Kanchipuram", "Kumarapalayam", "Karaikudi",
    "Neyveli", "Cuddalore", "Kumbakonam", "Tiruvannamalai", "Pollachi", "Rajapalayam",
    "Gudiyatham", "Pudukkottai", "Vaniyambadi", "Ambur", "Nagapattinam", "Viluppuram",
  ];
  const [heroCity, setHeroCity] = useState("Detecting location…");
  const [heroDetectingGPS, setHeroDetectingGPS] = useState(false);

  useEffect(() => { detectHeroLocation(); }, []);

  /* Reverse-geocode via Nominatim, trying progressively wider zoom levels
     until a usable place name comes back — down to village/hamlet/suburb
     level — instead of forcing a match against the fixed city list.

     Label is built as "<small place>, <city>" (e.g. "Iyyapanthangal,
     Chennai"), not "<small place>, <state>". Mirrors Header.js's
     detectCurrentLocation exactly, including debug logging and the
     display_name fallback, so both pickers behave identically. */
  const detectHeroLocation = () => {
    if (!navigator.geolocation) {
      setHeroCity("Chennai");
      return;
    }
    setHeroDetectingGPS(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        console.log("[location] coords:", latitude, longitude);

        // zoom=14 → neighbourhood level (Nominatim's own mapping: 13=village/
        // suburb, 14=neighbourhood, 16-17=streets, 18=building). Building-level
        // zoom can actually return LESS address detail if no building exists
        // exactly at that point, so neighbourhood-first is more reliable.
        const zoomLevels = [14, 16, 13, 18, 10];
        let resolved = false;

        for (const zoom of zoomLevels) {
          try {
            const res = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}&zoom=${zoom}&addressdetails=1`
            );
            const data = await res.json();
            const a = data.address || {};

            // Full debug output — check the browser console to see exactly
            // what OSM has tagged for this point at each zoom level.
            console.log(`[location] zoom=${zoom} address:`, a, "display_name:", data.display_name);

            // Widened fallback list — different areas use different OSM tags
            const small =
              a.neighbourhood || a.suburb || a.quarter || a.residential ||
              a.village || a.hamlet || a.city_district || a.borough || "";

            const city =
              a.city || a.town || a.municipality || a.county || "";

            let label = "";
            if (small && city && small !== city) {
              label = `${small}, ${city}`;
            } else if (small) {
              label = small;
            } else if (city) {
              label = city;
            }

            if (label) {
              setHeroCity(label);
              resolved = true;
              break; // stop as soon as we find a usable name
            }
          } catch (err) {
            console.log(`[location] zoom=${zoom} fetch failed:`, err);
          }
        }

        // Last resort: OSM has no tagged locality at any zoom for this exact
        // point — try to pull the first meaningful segment out of display_name
        // instead of giving up entirely.
        if (!resolved) {
          try {
            const res = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}&zoom=16&addressdetails=1`
            );
            const data = await res.json();
            if (data.display_name) {
              const parts = data.display_name.split(",").map((p) => p.trim());
              // Skip the first part if it looks like a house number / plot id
              const label = /^\d+$/.test(parts[0]) ? parts.slice(1, 3).join(", ") : parts.slice(0, 2).join(", ");
              if (label) {
                setHeroCity(label);
                resolved = true;
              }
            }
          } catch (err) {
            console.log("[location] display_name fallback failed:", err);
          }
        }

        if (!resolved) setHeroCity("Chennai"); // absolute last-resort fallback
        setHeroDetectingGPS(false);
      },
      (err) => {
        console.log("[location] geolocation error:", err);
        setHeroCity("Chennai"); // permission denied / unavailable
        setHeroDetectingGPS(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  /* ── Hero right-side banner slider (single full image per slide, no text
     overlay — the banners already have their own text baked in).
     Plain React state, no Swiper — a fixed-height carousel library was
     fighting the image's real aspect ratio and cropping it down to just
     the background colour. A plain <img> with width:100%/height:auto can
     never do that, so this is now just an index + two buttons. ── */
  /* Hero banners now come from the same bannerData API as everything else —
     filtered by banner_type === "hero" (Active only). Falls back to a single
     safe placeholder slide if none are configured yet, so the slider never
     breaks. */
  const heroBannersRaw = banners.filter((b) => b.banner_type === "hero" && b.status === "Active");
  const heroBanners = heroBannersRaw.length > 0
    ? heroBannersRaw.map((b) => ({
        img: b.image ? `${API_BASE_URL}public/banner/${b.image}` : `${API_BASE_URL}public/noimage.jpg`,
        alt: b.name || "Ayurvedic Wellness",
        to: b.link || null,
      }))
    : [{ img: `${API_BASE_URL}public/noimage.jpg`, alt: "Ayurvedic Wellness", to: null }];

  const [heroBannerIdx, setHeroBannerIdx] = useState(0);
  useEffect(() => { setHeroBannerIdx(0); }, [heroBanners.length]);
  const heroBannerPrev = () => setHeroBannerIdx((i) => (i - 1 + heroBanners.length) % heroBanners.length);
  const heroBannerNext = () => setHeroBannerIdx((i) => (i + 1) % heroBanners.length);
  useEffect(() => {
    if (heroBanners.length <= 1) return;
    const t = setInterval(() => setHeroBannerIdx((i) => (i + 1) % heroBanners.length), 5000);
    return () => clearInterval(t);
  }, [heroBanners.length]);

  /* ── Exclusive Discounts banner slider — image only, no text overlay.
     Now dynamic: pulled from the same bannerData API, filtered by
     banner_type === "exclusive" (Active only). Falls back to a single
     safe placeholder slide if none are configured yet. ── */
  const dealBannersRaw = banners.filter((b) => b.banner_type === "exclusive" && b.status === "Active");
  const dealBanners = dealBannersRaw.length > 0
    ? dealBannersRaw.map((b) => ({
        img: b.image ? `${API_BASE_URL}public/banner/${b.image}` : `${API_BASE_URL}public/noimage.jpg`,
        alt: b.name || "Exclusive Discount",
        to: b.link || "/ayush-product-list?type=offer",
      }))
    : [{ img: `${API_BASE_URL}public/noimage.jpg`, alt: "Exclusive Discount", to: "/ayush-product-list?type=offer" }];

  const [dealBannerIdx, setDealBannerIdx] = useState(0);
  useEffect(() => { setDealBannerIdx(0); }, [dealBanners.length]);
  const dealBannerPrev = () => setDealBannerIdx((i) => (i - 1 + dealBanners.length) % dealBanners.length);
  const dealBannerNext = () => setDealBannerIdx((i) => (i + 1) % dealBanners.length);
  useEffect(() => {
    if (dealBanners.length <= 1) return;
    const t = setInterval(() => setDealBannerIdx((i) => (i + 1) % dealBanners.length), 4200);
    return () => clearInterval(t);
  }, [dealBanners.length]);

  /* Small reusable chevron arrow (real inline SVG, brand-coloured via
     currentColor so it inherits the button's CSS color on hover) */
  const HeroChevron = ({ flip }) => (
    <svg
      width="20" height="32" viewBox="0 0 20 32" fill="none"
      style={flip ? { transform: "scaleX(-1)" } : undefined}
      aria-hidden="true"
    >
      <path d="M2 2L18 16L2 30" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  /* ── Feature Brands — real API data (bottom-type banners: clustered product
     photography + name + short_desc + link, exactly like the reference) ── */
  const featureBrandBanners = banners.filter((b) => b.banner_type === "bottom" && b.status === "Active");

  /* ════════════════════════════════════════════════════════
     RENDER — matches Health Concern mockup
  ════════════════════════════════════════════════════════ */
  return (
    <div className="hc-home">

      <HomeLoader visible={showPageLoader} />

      {/* Header search overlay — uses allProductsRef + allDoctorsRef + allTreatmentsRef + allClinicsRef */}
      <HeaderSearchOverlay
        visible={scrolled}
        anchorRect={anchorRect}
        allProductsRef={allProductsRef}
        allDoctorsRef={allDoctorsRef}
        allTreatmentsRef={allTreatmentsRef}
        allClinicsRef={allClinicsRef}
      />

      {/* ═══════════════ ANNOUNCEMENT BAR ═══════════════ */}
      <div className="hc-announce">
        <div className="container">
          <div className="hc-announce-row">
            <span><i className="fa-solid fa-stethoscope" /> Free Doctor Consultation for New Users</span>
            <span><i className="fa-solid fa-leaf" /> 100% Authentic Ayurvedic Products</span>
            <span><i className="fa-solid fa-star" /> 6L+ Happy Patients</span>
            <span className="hc-announce-right"><Link to="/"><i className="fa-solid fa-mobile-screen" /> Download App</Link></span>
            <span className="hc-announce-right"><Link to="/orderlist"><i className="fa-solid fa-truck" /> Track Order</Link></span>
          </div>
        </div>
      </div>

      <Header />

      {/* ═══════════════ HERO (reference layout: headline, location+search pill, quick actions, program links, 2x2 image grid) ═══════════════ */}
      <section className="hc-hero3">

        <div className="container">
          <div className="hc-hero3-grid">

            {/* LEFT — headline, location+search pill, quick actions, program links */}
            <div className="hc-hero3-left">
              <h1 className="hc-hero3-title">
                {(menuData?.hero_title
                  ? menuData.hero_title
                  : "Consult trusted Ayurvedic doctors\n& order authentic medicines online"
                )
                  .split("\n")
                  .map((line, i, arr) => (
                    <React.Fragment key={i}>
                      {line}
                      {i < arr.length - 1 && <br />}
                    </React.Fragment>
                  ))}
              </h1>

              {/* Combined location + search pill — safe now that Trending chips are removed (single-row height) */}
              <div className="hc-hero3-searchbar">
                <div className="hc-hero3-city">
                  <CityPicker
                    cities={tamilNaduCities}
                    value={heroCity}
                    onChange={setHeroCity}
                    onDetectGPS={detectHeroLocation}
                    detecting={heroDetectingGPS}
                  />
                </div>
                <span className="hc-hero3-divider" aria-hidden="true" />
                <div className="autocomplete hc-hero3-searchwrap">
                  <HeroSearch
                    allProductsRef={allProductsRef}
                    allDoctorsRef={allDoctorsRef}
                    allTreatmentsRef={allTreatmentsRef}
                    allClinicsRef={allClinicsRef}
                    navigate={navigate}
                    categories={categories}
                    selected={selected}
                  />
                </div>
              </div>

              {/* Quick action row */}
              <div className="hc-hero3-actions">
                <a href="tel:+919500946631" className="hc-hero3-action tone-blue">
                  <span className="hc-hero3-action-icon"><i className="fa-solid fa-phone" /></span>
                  <span>Book via Phone Call</span>
                </a>
                <Link to="/ayush-product-list" className="hc-hero3-action tone-pink">
                  <span className="hc-hero3-action-icon"><i className="fa-solid fa-bolt" /></span>
                  <span>Quick Order</span>
                </Link>
                <Link to="/doctor-list-page" className="hc-hero3-action tone-green">
                  <span className="hc-hero3-action-icon"><i className="fa-solid fa-video" /></span>
                  <span>Online Consultation</span>
                </Link>
              </div>

              {/* Health program links — replaces "Find tests & packages" panel */}
              <div className="hc-hero3-programs">
                <p className="hc-hero3-programs-label">Community health programs</p>
                <div className="hc-hero3-programs-row">
                  {healthPrograms.map((p, i) => (
                    <Link to={p.to} key={i} className="hc-hero3-program-btn">
                      <span><i className={`fa-solid ${p.icon}`} /></span>
                      {p.title}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {/* RIGHT — single full-image banner slider, big external prev/next arrows, no text overlay */}
            <div className="hc-hero3-right">
              <div className="hc-hero3-bannerwrap">
                <button
                  type="button"
                  className="hc-hero3-navbtn hc-hero3-navbtn-prev"
                  aria-label="Previous banner"
                  onClick={heroBannerPrev}
                >
                  <HeroChevron flip />
                </button>

                <div className="hc-hero3-bannerslider-inner">
                   {heroBanners[heroBannerIdx].to ? (
                    
                    <a  href={heroBanners[heroBannerIdx].to}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hc-hero3-bannerslide"
                    >
                      <img
                        src={heroBanners[heroBannerIdx].img}
                        alt={heroBanners[heroBannerIdx].alt}
                        onError={(e) => { e.currentTarget.src = `${API_BASE_URL}public/noimage.jpg`; }}
                      />
                    </a>
                  ) : (
                    <div className="hc-hero3-bannerslide">
                      <img
                        src={heroBanners[heroBannerIdx].img}
                        alt={heroBanners[heroBannerIdx].alt}
                        onError={(e) => { e.currentTarget.src = `${API_BASE_URL}public/noimage.jpg`; }}
                      />
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  className="hc-hero3-navbtn hc-hero3-navbtn-next"
                  aria-label="Next banner"
                  onClick={heroBannerNext}
                >
                  <HeroChevron />
                </button>
              </div>
            </div>

          </div>
        </div>
      </section>


     {/* ═══════════════ TOP SPECIALISTS ═══════════════ */}
      <section className="hc-specialists section-padding pt-0">
        <div className="container">
          <div className="hc-section-head">
            <div>
              <span className="hc-kicker">Verified Specialists</span>
              <h2 className="hc-h2">Top Ayurvedic <span>Specialists</span></h2>
            </div>
            <Link to="/doctor-list-page" className="hc-view-all">View all doctors <i className="fa-solid fa-arrow-right" /></Link>
          </div>
          <div className="hc-spec-grid">
            {loadingDoctors
              ? [...Array(4)].map((_, i) => (
                <div className="hc-spec-card hb-skeleton-card" key={i}>
                  <div className="hc-spec-row">
                    <Skeleton width={58} height={58} borderRadius={50} />
                    <div style={{ flex: 1 }}>
                      <Skeleton width="80%" height={13} style={{ marginBottom: 7 }} />
                      <Skeleton width="60%" height={11} style={{ marginBottom: 7 }} />
                      <Skeleton width="45%" height={11} />
                    </div>
                  </div>
                  <Skeleton width="100%" height={40} borderRadius={10} style={{ marginTop: 14 }} />
                </div>
              ))
              : (menuData?.top_specialist?.length > 0 ? menuData.top_specialist : doctors).slice(0, 4).map((doctor) => (
                <div className="hc-spec-card" key={doctor.id}>
                  <span className={`hc-spec-status ${doctor.is_active ? "" : "off"}`}>
                    <i className="fa-solid fa-circle" /> {doctor.is_active ? "Available Today" : "Available Tomorrow"}
                  </span>
                  <div className="hc-spec-row">
                    <div className="hc-spec-avatar">
                      <Link to={`/doctordetails/${doctor.slug}`}>
                        <img
                          src={doctor.images ? `${API_BASE_URL}public/doctorimages/${doctor.images.split(",")[0]}` : `${API_BASE_URL}public/doctorimages/doctor-placeholder.jpg`}
                          alt={doctor.name}
                        />
                      </Link>
                    </div>
                    <div className="hc-spec-info">
                      <h4 className="hc-spec-name"><Link to={`/doctordetails/${doctor.slug}`}>{doctor?.name}</Link></h4>
                      <p className="hc-spec-dept">{doctor?.department_title?.name}</p>
                      <p className="hc-spec-qual">{doctor?.specializations}</p>
                      {doctor?.experience && <p className="hc-spec-exp">{doctor.experience} Exp.</p>}
                      <div className="hc-spec-rating">
                        <i className="fa-solid fa-star" /> {(() => { const r = getDoctorRatingSummary(doctor); return r.count ? r.average.toFixed(1) : "New"; })()}
                        <span>{`(${getDoctorRatingSummary(doctor).count})`}</span>
                      </div>
                    </div>
                  </div>
                  <div className="hc-spec-foot">
                    <div className="hc-spec-fee">
                      {doctor?.videocall_price == 0
                        ? <span className="hc-spec-free">Free</span>
                        : <><b>₹{doctor?.videocall_price}</b><span>Consultation Fee</span></>}
                    </div>
                    <Link to={`/doctordetails/${doctor.slug}`} className="hc-btn-primary hc-btn-sm">Consult Now</Link>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ POPULAR TREATMENTS ═══════════════ */}
      <section className="hc-treatments section-padding pt-0">
        <div className="container">
          <div className="hc-section-head">
            <div>
              <span className="hc-kicker">Our</span>
              <h2 className="hc-h2">Popular <span>Treatments</span></h2>
            </div>
            <Link to="/treatments" className="hc-view-all">View all treatments <i className="fa-solid fa-arrow-right" /></Link>
          </div>
          <div className="hc-treat-grid">
            {loadingTreatments
              ? [...Array(7)].map((_, i) => <CategorySkeleton key={i} />)
              : menuData?.treatment?.slice(0, 7).map((item, index) => (
                <Link to={`/treatment/${item.slug}`} className="hc-treat-card" key={item.id || index}>
                  <img
                    src={item.image ? `${API_BASE_URL}public/treatment/${item.image.split(",")[0]}`
                      : item.icon ? `${API_BASE_URL}public/treatment_icon/${item.icon.split(",")[0]}`
                      : `${API_BASE_URL}public/noimage.jpg`}
                    alt={item.name} loading="lazy"
                  />
                  <div className="hc-treat-overlay">
                    <h4>{item.name.replace(/\b\w/g, (c) => c.toUpperCase())}</h4>
                    <span className="hc-treat-book">Book Now <i className="fa-solid fa-arrow-right" /></span>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ RECOMMENDED DOCTORS (classic cards, full data) ═══════════════ */}
      <section className="hc-recdocs section-padding pt-0">
        <div className="container">
          <div className="hc-section-head">
            <div>
              <span className="hc-kicker">Handpicked for You</span>
              <h2 className="hc-h2">Recommended <span>Doctors</span></h2>
            </div>
            <Link to="/doctor-list-page" className="hc-view-all">View all doctors <i className="fa-solid fa-arrow-right" /></Link>
          </div>

          <div className="dc-grid hc-recdoc-classic-grid">
            {loadingDoctors
              ? [...Array(16)].map((_, i) => (
                <div className="dc-skel-card" key={i} style={{ animationDelay: `${i * 0.08}s` }}>
                  <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                    <div className="dc-skel" style={{ width: 78, height: 78, borderRadius: "50%", flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div className="dc-skel" style={{ width: "75%", height: 15, marginBottom: 8 }} />
                      <div className="dc-skel" style={{ width: "50%", height: 11, marginBottom: 8 }} />
                      <div className="dc-skel" style={{ width: "40%", height: 11, marginBottom: 6 }} />
                      <div className="dc-skel" style={{ width: "55%", height: 11 }} />
                    </div>
                  </div>
                  <div style={{ height: 1, background: "#f1f5f9", margin: "16px 0" }} />
                  <div className="dc-skel" style={{ width: "85%", height: 11, marginBottom: 6 }} />
                  <div className="dc-skel" style={{ width: "65%", height: 11, marginBottom: 16 }} />
                  <div style={{ height: 1, background: "#f1f5f9", marginBottom: 16 }} />
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div className="dc-skel" style={{ width: 130, height: 40, borderRadius: 50 }} />
                    <div className="dc-skel" style={{ width: 60, height: 24, borderRadius: 8 }} />
                  </div>
                </div>
              ))
              : doctors.slice(0, 16).map((doctor) => (
                <div className="dc-card" key={doctor.id}>

                  {/* Top: Avatar + Info */}
                  <div className="dc-card-top">
                    <div className="dc-avatar-wrap">
                      <div className="dc-avatar">
                        <Link to={`/doctordetails/${doctor.slug}`}>
                          <img
                            src={doctor.images
                              ? `${API_BASE_URL}public/doctorimages/${doctor.images.split(",")[0]}`
                              : `${API_BASE_URL}public/doctorimages/doctor-placeholder.jpg`}
                            alt={doctor.name}
                          />
                        </Link>
                      </div>
                      {/* online / offline indicator dot */}
                      <div className={`dc-online-dot ${!doctor.is_active ? "dc-inactive" : ""}`} title={doctor.is_active ? "Online" : "Offline"} />
                    </div>
                    <div className="dc-info">
                      <h4 className="dc-name fw-semibold">
                        <Link to={`/doctordetails/${doctor.slug}`}>{doctor?.name}</Link>
                      </h4>
                      <div className="dc-dept">
                        <i className="fa-solid fa-stethoscope" />
                        {doctor?.department_title?.name}
                      </div>
                      <div className="dc-rating">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <i key={n} className={n <= Math.round(getDoctorRatingSummary(doctor).average) ? "fa-solid fa-star" : "fa-regular fa-star"} />
                        ))}
                        <span className="dc-rating-score">
                          {getDoctorRatingSummary(doctor).count ? `(${getDoctorRatingSummary(doctor).average.toFixed(1)}/5)` : "(No reviews yet)"}
                        </span>
                      </div>
                      <div className="dc-verified">
                        <i className="fa-solid fa-circle-check" /> Verified
                      </div>
                    </div>
                  </div>

                  {/* Body: Description + Location (dynamic, from API) */}
                  <div className="dc-card-body m-0">
                    <p className="dc-desc">
                      {doctor?.experience && `${doctor.experience} experience`}
                      {doctor?.short_desc
                        ? ` — ${doctor.short_desc}`
                        : doctor?.specializations
                          ? ` — ${doctor.specializations}`
                          : ""}
                    </p>

                    {doctor?.clinics?.length > 0 ? (
                      doctor.clinics.map((clinicItem, index) => (
                        <div className="dc-location" key={index}>
                          <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(clinicItem.address || "")}`} target="_blank" rel="noopener noreferrer" className="w-100 d-block">
                            <i className="fa fa-location-dot mb-0"></i>
                            {clinicItem.address || `${doctor?.department_title?.name || "Physician"}, ${doctor?.city || ""}`}
                          </a>
                        </div>
                      ))
                    ) : (doctor?.address || doctor?.city) && (
                      <div className="dc-location">
                        <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${doctor.address || ""} ${doctor.city || ""} ${doctor.pincode || ""}`)}`} target="_blank" rel="noopener noreferrer" className="w-100 d-block">
                          <i className="fa fa-location-dot mb-0"></i>
                          {doctor?.department_title?.name}, {doctor?.city}{doctor?.pincode ? ` - ${doctor.pincode}` : ""}
                        </a>
                      </div>
                    )}
                  </div>

                  {/* Footer: Book + Price */}
                  <div className="dc-card-footer">
                    <Link to={`/doctordetails/${doctor.slug}`} className="dc-book-btn">
                      <i className="fa-solid fa-calendar-check" />
                      <span>Book Consultation</span>
                    </Link>
                    <div className="dc-price">
                      {doctor?.videocall_price == 0 ? (
                        <span className="dc-price-free">Free</span>
                      ) : (
                        <>
                          <span className="dc-price-label">From</span>
                          <span className="dc-price-value fw-semibold">₹{doctor?.videocall_price}</span>
                        </>
                      )}
                    </div>
                  </div>

                </div>
              ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ BROWSE BY HEALTH CONCERN ═══════════════ */}
      <section className="hc-concern section-padding pt-0">
        <div className="container">
          <div className="hc-section-head">
            <div>
              <span className="hc-kicker">Find Care For</span>
              <h2 className="hc-h2">Browse by <span>Health Concern</span></h2>
            </div>
            <Link to="/category-list" className="hc-view-all">View all <i className="fa-solid fa-arrow-right" /></Link>
          </div>
          <div className="hc-concern-grid">
            {loadingCategories
              ? [...Array(6)].map((_, i) => <CategorySkeleton key={i} />)
              : menuData?.health_concern?.slice(0, 6).map((cat, index) => (
                <Link to={`/category/${cat.slug}`} className={`hc-concern-card tone-${(index % 5) + 1}`} key={cat.id || index}>
                  <div className="hc-concern-img">
                    <img
                      src={cat.image ? `${API_BASE_URL}public/category/${cat.image}` : `${API_BASE_URL}public/noimage.jpg`}
                      alt={cat.name}
                      onError={(e) => { e.currentTarget.src = `${API_BASE_URL}public/noimage.jpg`; }}
                    />
                  </div>
                  <h4>{cat.name.charAt(0).toUpperCase() + cat.name.slice(1)}</h4>
                  <span className="hc-concern-arrow"><i className="fa-solid fa-arrow-right" /></span>
                </Link>
              ))}
          </div>
        </div>
      </section>
      
      {/* ═══════════════ DOCTOR RECOMMENDED PRODUCTS ═══════════════ */}
      <section className="hc-products hc-products-1 section-padding pt-0">
        <div className="container">
          <div className="hc-section-head">
            <div>
              <span className="hc-kicker">Doctor Recommended</span>
              <h2 className="hc-h2">Recommended <span>Medicines</span></h2>
            </div>
            <Link to="/ayush-product-list" className="hc-view-all">View all Medicines <i className="fa-solid fa-arrow-right" /></Link>
          </div>
          <div className="hc-prod-grid">
            {loadingTabProducts
              ? [...Array(5)].map((_, i) => <ProductSkeleton key={i} />)
              : filteredProducts.slice(0, 5).map((product) => (
                <div className="hc-prod-card" key={product.id}>
                  <span className="hc-prod-tag"><i className="fa-solid fa-circle-check" /> Hot Sale</span>
                  <div className="hc-prod-img">
                    <Link to={`/product/${product.slug}`}>
                      <img src={product.images ? `${API_BASE_URL}public/productimages/${product.images.split(",")[0]}` : `${API_BASE_URL}public/noimage.jpg`} alt={product.name} />
                    </Link>
                    <button type="button" className="hc-prod-wish" onClick={(e) => handleWishlist(e, product.id)} aria-label="Wishlist">
                      <i className={wishlist?.some?.((item) => item.id === product.id) ? "fa-solid fa-heart" : "fa-regular fa-heart"} />
                    </button>
                  </div>
                  <div className="hc-prod-body">
                    <Link to={`/product/${product.slug}`}><h4 className="hc-prod-name">{product.name}</h4></Link>
                    <p className="hc-prod-cat">{product?.category_title?.name}</p>
                    <div className="hc-prod-rating">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <i key={n} className={n <= Math.round(product?.average_rating || 0) ? "fa-solid fa-star" : "fa-regular fa-star"} />
                      ))}
                      <span>{product?.review_count ? `(${product.review_count})` : "(0)"}</span>
                    </div>
                    <div className="hc-prod-price">
                      {product.dprice
                        ? <><b>₹{product.price - (product.price * product.dprice / 100)}</b><del>₹{product.price}</del></>
                        : <b>₹{product.price}</b>}
                    </div>
                    {product.stock_available === "In Stock" ? (
                      <button
                        type="button"
                        className="hc-btn-primary hc-prod-cart"
                        onClick={(e) => {
                          flyToCart(e.currentTarget.closest(".hc-prod-card")?.querySelector(".hc-prod-img"));
                          addToCart(product.id, 1);
                        }}
                      >
                        <i className="fa-solid fa-cart-shopping" /> Add to Cart
                      </button>
                    ) : (
                      <button type="button" disabled className="hc-prod-cart hc-prod-oos"><i className="fa-solid fa-cart-shopping" /> Out of Stock</button>
                    )}
                  </div>
                </div>
              ))}
          </div>
        </div>
      </section>


      {/* ═══════════════ QUICK SERVICES ═══════════════ */}
      {/*<section className="hc-services section-padding">
        <div className="container">
          <div className="hc-section-head">
            <div>
              <span className="hc-kicker">Quick Access to</span>
              <h2 className="hc-h2">Healthcare <span>Services</span></h2>
            </div>
            <Link to="/treatments" className="hc-view-all">View all services <i className="fa-solid fa-arrow-right" /></Link>
          </div>
          <div className="hc-services-row">
            {quickServices.map((s, i) => (
              <Link to={s.to} key={i} className="hc-service-card">
                <span className="hc-service-icon"><i className={`fa-solid ${s.icon}`} /></span>
                <span className="hc-service-title">{s.title}</span>
              </Link>
            ))}
            <button type="button" className="hc-service-next" aria-label="More services"><i className="fa-solid fa-chevron-right" /></button>
          </div>
        </div>
      </section>*/}

      {/* ═══════════════ AI SYMPTOM CHECKER ═══════════════ */}
      <section className="hc-symptom section-padding pt-0">
        <div className="container">
          <div className="hc-symptom-card">
            <div className="hc-symptom-left">
              <span className="hc-chip-tag"><i className="fa-solid fa-user-doctor" /> For Healthcare Professionals</span>
              <h2 className="hc-symptom-title">Are You an Ayurvedic Doctor?<br /> <span className="hc-accent-text">Join Our Growing Network</span></h2>
              <p className="hc-symptom-sub">Reach thousands of patients online, manage consultations effortlessly, and grow your practice with Aceveda already trusted by 500+ Expert Doctors onboarded.</p>
              <div className="hc-symptom-cta">
                <Link to="/doctor-register" className="hc-btn-primary">Register Now<i className="fa-solid fa-handshake" /></Link>
                <div className="hc-symptom-how">
                  <div className="hc-float-avatars">
                    <img src="./front/assets/images/avatar.png" alt="" />
                    <img src="./front/assets/images/avatar.png" alt="" />
                    <img src="./front/assets/images/avatar.png" alt="" />
                  </div>
                  <span>How it works?</span>
                </div>
              </div>
            </div>

            <div className="hc-symptom-mid">
              <p className="hc-symptom-midlabel">Popular Locations</p>
              <div className="hc-symptom-chips">
                {symptomChips.map((c, i) => (
                  <button type="button" key={i} className="hc-chip" onClick={() => navigate("/treatments")}>{c}</button>
                ))}
              </div>
            </div>

            <div className="hc-symptom-right">
              <img className="hc-symptom-logo" src="./front/assets/images/logo.png" alt="Shree Varma"
                onError={(e) => { e.currentTarget.style.display = "none"; }} />
              <div className="hc-phone">
                <div className="hc-phone-notch" />
                <div className="hc-phone-screen hc-phone-screen-docs">
                  <span className="hc-phone-brand"><i className="fa-solid fa-user-doctor" /> Our Doctors</span>
                  {/* <div className="hc-phone-doclist">
                    {(loadingDoctors ? [...Array(4)] : doctors.slice(0, 4)).map((doctor, i) => (
                      <div className="hc-phone-docrow" key={doctor?.id || i}>
                        <img
                          src={doctor?.images ? `${API_BASE_URL}public/doctorimages/${doctor.images.split(",")[0]}` : `${API_BASE_URL}public/doctorimages/doctor-placeholder.jpg`}
                          alt={doctor?.name || "Doctor"}
                        />
                        <div>
                          <strong>{doctor?.name || "Loading…"}</strong>
                          <span>{doctor?.department_title?.name || ""}</span>
                        </div>
                        <i className="fa-solid fa-circle-check hc-phone-docverified" />
                      </div>
                    ))}
                  </div> */}

                  <div className="hc-phone-doclist">
                        {(loadingDoctors ? [...Array(4)] : doctors.slice(0, 4)).map((doctor, i) => (
                          <div className="hc-phone-docrow hc-tooltip-wrap" key={doctor?.id || i}>
                            <img
                              src={doctor?.images ? `${API_BASE_URL}public/doctorimages/${doctor.images.split(",")[0]}` : `${API_BASE_URL}public/doctorimages/doctor-placeholder.jpg`}
                              alt={doctor?.name || "Doctor"}
                            />
                            <div>
                              <strong>{doctor?.name || "Loading…"}</strong>
                              <span>{doctor?.department_title?.name || ""}</span>
                            </div>
                            <i className="fa-solid fa-circle-check hc-phone-docverified" />

                            {/* Tooltip — only render once real doctor data has loaded */}
                            {doctor?.name && (
                              <div className="hc-phone-tooltip">
                                <strong>{doctor.name}</strong>
                                {doctor?.specializations && <p>{doctor.specializations}</p>}
                                {doctor?.experience && <span>{doctor.experience} Exp.</span>}
                                <span className="hc-phone-tooltip-fee">
                                  {doctor?.videocall_price == 0 ? "Free Consultation" : `₹${doctor?.videocall_price} Consultation`}
                                </span>
                              </div>
                            )}
                          </div>
                        ))}
                  </div>
                 
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      

   {/* ═══════════════ EXCLUSIVE DISCOUNT — image-only banner slider ═══════════════ */}
      <section className="hc-deals section-padding pt-0">
        <div className="container">
          <div className="hc-section-head">
            <div>
              <span className="hc-kicker">Limited Time</span>
              <h2 className="hc-h2">Exclusive <span>Discounts</span></h2>
            </div>
          </div>

          {loadingBanners ? (
            <div className="hc-deals-skel-row">
              <Skeleton width="100%" height={260} borderRadius={24} />
              <Skeleton width="100%" height={260} borderRadius={24} />
            </div>
          ) : (
            <div className="hc-deals-bannerwrap">
              <button
                type="button"
                className="hc-hero3-navbtn hc-hero3-navbtn-prev"
                aria-label="Previous discount banner"
                onClick={dealBannerPrev}
              >
                <HeroChevron flip />
              </button>

              <div className="hc-deals-banner-grid">
                {Array.from({ length: Math.min(2, dealBanners.length) }).map((_, offset) => {
                  const banner = dealBanners[(dealBannerIdx + offset) % dealBanners.length];
                  return (
                    <Link to={banner.to} className="hc-deals-bannerslide" key={offset}>
                      <img src={banner.img} alt={banner.alt} />
                    </Link>
                  );
                })}
              </div>

              <button
                type="button"
                className="hc-hero3-navbtn hc-hero3-navbtn-next"
                aria-label="Next discount banner"
                onClick={dealBannerNext}
              >
                <HeroChevron />
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ═══════════════ FEATURE BRANDS ═══════════════ */}
      <section className="hc-brands hc-brand-1 section-padding pt-0">
        <div className="container">
          <div className="hc-brands-card">
            <div className="hc-brands-orb hc-brands-orb-1" aria-hidden="true" />
            <div className="hc-brands-orb hc-brands-orb-2" aria-hidden="true" />

            {/* Left — real banner image cluster (bottom-type banner, dynamic from API) */}
            <div className="hc-brands-side hc-brands-side-left">
              {!loadingBanners && featureBrandBanners[0] && (
                <a
                  href={featureBrandBanners[0].link || undefined}
                  {...(featureBrandBanners[0].link ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  onClick={(e) => { if (!featureBrandBanners[0].link) e.preventDefault(); }}
                >
                  <img
                    src={featureBrandBanners[0].image ? `${API_BASE_URL}public/banner/${featureBrandBanners[0].image}` : `${API_BASE_URL}public/noimage.jpg`}
                    alt={featureBrandBanners[0].name || "Featured products"}
                    onError={(e) => { e.currentTarget.src = `${API_BASE_URL}public/noimage.jpg`; }}
                  />
                </a>
              )}
            </div>

            <div className="hc-brands-inner">
              <h2 className="hc-h2 text-center">{featureBrandBanners[0]?.name || "Our Feature Brands"}</h2>
              <p className="hc-brands-sub">{featureBrandBanners[0]?.short_desc || "Reduce your Medical Expenses with Membership"}</p>
              <Link to="/ayush-product-list" className="hc-btn-primary hc-brands-btn">Explore Now <i className="fa-solid fa-chevron-right" /></Link>
            </div>

            {/* Right — second banner image cluster (dynamic from API) */}
            <div className="hc-brands-side hc-brands-side-right">
              {!loadingBanners && featureBrandBanners[1] && (
                <a
                  href={featureBrandBanners[1].link || undefined}
                  {...(featureBrandBanners[1].link ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  onClick={(e) => { if (!featureBrandBanners[1].link) e.preventDefault(); }}
                >
                  <img
                    src={featureBrandBanners[1].image ? `${API_BASE_URL}public/banner/${featureBrandBanners[1].image}` : `${API_BASE_URL}public/noimage.jpg`}
                    alt={featureBrandBanners[1].name || "Featured products"}
                    onError={(e) => { e.currentTarget.src = `${API_BASE_URL}public/noimage.jpg`; }}
                  />
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════ STATS + WELLNESS CARD ═══════════════ */}
      <section className="hc-statsrow section-padding pt-0">
        <div className="container">
          <div className="hc-statsrow-grid">
            <div className="hc-stats2">
              {stats.map((s, i) => (
                <div className="hc-stat2" key={i}>
                  <span className="hc-stat2-icon"><i className={`fa-solid ${s.icon}`} /></span>
                  <div>
                    <span className="hc-stat2-value">{s.value}</span>
                    <span className="hc-stat2-label">{s.label}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="hc-wellness">
              <h3>Start your wellness journey today!</h3>
              <p>Book a consultation and take the first step towards better health.</p>
              <Link to="/doctor-list-page" className="hc-btn-primary">Book Now <i className="fa-solid fa-arrow-right" /></Link>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════ HOW IT WORKS + TESTIMONIALS ═══════════════ */}
      <section className="hc-hiwrow section-padding pt-0">
        <div className="container">
          <div className="hc-hiwrow-grid">

            {/* How it works */}
            <div className="hc-hiw">
              <h3 className="hc-block-title">How It Works</h3>
              <p className="hc-block-sub">Simple steps to better health with Ayurveda</p>
              <div className="hc-hiw-steps">
                {steps.map((st, i) => (
                  <div className="hc-hiw-step" key={i}>
                    <span className="hc-hiw-no">{st.no}</span>
                    <span className="hc-hiw-icon"><i className={`fa-solid ${st.icon}`} /></span>
                    <h4>{st.title}</h4>
                    <p>{st.desc}</p>
                    {i < steps.length - 1 && <span className="hc-hiw-arrow"><i className="fa-solid fa-arrow-right" /></span>}
                  </div>
                ))}
              </div>
            </div>

            {/* Testimonials */}
            <div className="hc-testi">
              <div className="hc-testi-head">
                <div>
                  <h3 className="hc-block-title">What Our Patients Say</h3>
                  <p className="hc-block-sub">Real stories from real people</p>
                </div>
                <Link to="/blog" className="hc-testi-viewall">View all reviews <i className="fa-solid fa-arrow-right" /></Link>
              </div>
              <div className="hc-testi-card">
                <div className="hc-testi-top">
                  <img src="./front/assets/images/avatar.png" alt="" />
                  <div>
                    <strong>{testimonials[testiIdx].name}</strong>
                    <span>{testimonials[testiIdx].city}</span>
                  </div>
                  <div className="hc-testi-stars">
                    {[...Array(5)].map((_, s) => (
                      <i key={s} className={s < testimonials[testiIdx].stars ? "fa-solid fa-star" : "fa-regular fa-star"} />
                    ))}
                  </div>
                </div>
                <p className="hc-testi-quote">"{testimonials[testiIdx].text}"</p>
                <div className="hc-testi-dots">
                  {testimonials.map((_, d) => (
                    <span
                      key={d}
                      className={d === testiIdx ? "active" : ""}
                      onClick={() => setTestiIdx(d)}
                      style={{ cursor: "pointer" }}
                    />
                  ))}
                </div>
              </div>
              <div
                className="hc-testi-video"
                role="button"
                tabIndex={0}
                aria-label="Play patient review video"
                onClick={() => setShowVideoModal(true)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setShowVideoModal(true); }}
              >
                <img src="./front/assets/images/testimonial-video.png" alt="Patient review"
                  onError={(e) => { e.currentTarget.src = "./front/assets/images/banner-doctor.png"; }} />
                <button type="button" className="hc-testi-play" aria-label="Play patient review video" tabIndex={-1}><i className="fa-solid fa-play" /></button>
                {/* <div className="hc-testi-vinfo">
                  <strong>Rahul Mehta</strong>
                  <span>Mumbai</span>
                  <div className="hc-testi-stars sm"><i className="fa-solid fa-star" /><i className="fa-solid fa-star" /><i className="fa-solid fa-star" /><i className="fa-solid fa-star" /><i className="fa-solid fa-star" /></div>
                </div> */}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ═══════════════ BLOG (full width — App Download moved to footer) ═══════════════ */}
      <section className="hc-blogrow section-padding pt-0">
        <div className="container">
          <div className="hc-blog hc-blog-full">
            <div className="hc-blog-head">
              <h3 className="hc-block-title">Latest from Our Blog</h3>
              <Link to="/blog" className="hc-testi-viewall">View all blogs <i className="fa-solid fa-arrow-right" /></Link>
            </div>
            <div className="hc-blog-grid hc-blog-grid-full">
              {loadingBlog
                ? [...Array(4)].map((_, i) => (
                  <div className="hc-blog-card hb-skeleton-card" key={i}>
                    <Skeleton width="100%" height={140} borderRadius={0} />
                    <div style={{ padding: 14 }}>
                      <Skeleton width="90%" height={13} style={{ marginBottom: 8 }} />
                      <Skeleton width="70%" height={11} />
                    </div>
                  </div>
                ))
                : blogPosts.map((b, i) => (
                  <Link to={`/blog/${b.slug}`} className="hc-blog-card" key={i}>
                    <div className="hc-blog-img">
                      <img src={b.img} alt={b.title}
                        onError={(e) => { e.currentTarget.src = `${API_BASE_URL}public/noimage.jpg`; }} />
                    </div>
                    <div className="hc-blog-body">
                      <h4>{b.title}</h4>
                      <p>{b.desc}</p>
                      <span className="hc-blog-more">Read More <i className="fa-solid fa-arrow-right" /></span>
                    </div>
                  </Link>
                ))}
            </div>
          </div>
        </div>
      </section>

      <MobileBottomNav userId={userId} />

      {/* Patient review video popup */}
      {showVideoModal && (
        <div className="hc-video-modal" onClick={() => setShowVideoModal(false)}>
          <div className="hc-video-modal-inner" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="hc-video-modal-close" aria-label="Close video" onClick={() => setShowVideoModal(false)}>
              <i className="fa-solid fa-xmark" />
            </button>
            <div className="hc-video-modal-frame">
              <iframe
                src={`https://www.youtube.com/embed/${patientReviewVideoId}?autoplay=1&rel=0`}
                title="Patient review video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Home;